import { useEffect, useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import type { Analytics } from "@models/analytics.model";
import AdCard from "@/shared/components/AdCard";
import { Ad } from "@models/Ad.model";
import { AnalyticsService } from "@services/analytics/AnalyticsService";
import { clientDashboardEvents } from "../clientDashboardEvents";

type View = "all" | "top" | "running";

const VIEWS: { key: View; label: string; dataKey: string }[] = [
    { key: "all", label: "All", dataKey: "all-ads-list" },
    { key: "top", label: "Top Performing", dataKey: "top-ads-list" },
    { key: "running", label: "Running", dataKey: "running-ads-list" },
];

interface AdRow {
    ad_id: number;
    ad_name?: string;
    engagements?: number | string;
    motivational_principles?: string | null;
    behavioral_strategies?: string | null;
}

export default function AdsView({ analytics }: { analytics: Analytics }) {
    const [view, setView] = useState<View>("all");
    const swiperRef = useRef<SwiperType | null>(null);

    const active = VIEWS.find((v) => v.key === view)!;
    const ads: AdRow[] = analytics.charts?.[active.dataKey] ?? [];
    // Leaf ad by ad id, for every list at once; undefined until it arrives. A null leaf is an
    // ad outside the selected organizations, and its card shows the empty state.
    const [leaves, setLeaves] = useState<Map<number, Ad | null>>();

    useEffect(() => {
        setLeaves(undefined);
        const adIds = [...new Set(VIEWS.flatMap((v) =>
            ((analytics.charts?.[v.dataKey] ?? []) as AdRow[]).map((row) => row.ad_id)))];
        if (adIds.length === 0) return;

        let stale = false;
        new AnalyticsService()
            .getAdMedia([...clientDashboardEvents.orgIds], adIds)
            .then((results) => new Map(results.map((r) => [r.ad_id, r.ad])))
            // Already logged by the command. Every card shows the empty state, not a skeleton.
            .catch(() => new Map<number, Ad | null>())
            .then((byId) => { if (!stale) setLeaves(byId); });
        return () => { stale = true; };
    }, [analytics]);

    // The header keeps the name the analytics row reports, as it did before MPN-2341.
    const cardAd = (row: AdRow) => {
        const leaf = leaves?.get(row.ad_id);
        return leaf
            ? new Ad({ ...leaf, name: row.ad_name ?? leaf.name })
            : new Ad({ id: row.ad_id, name: row.ad_name });
    };

    return (
        <div className="dash-card">
            <div className="dash-card__head">
                <span className="dash-card__title">Ads</span>
                <div className="ads-toggle btn-group btn-group-sm">
                    {VIEWS.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            className={`btn ${view === key ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => setView(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="dash-card__body">
                {ads.length === 0 ? (
                    <div className="dash-empty">No ads available.</div>
                ) : (
                    <div style={{ position: "relative" }}>
                        <button
                            type="button"
                            className="ads-nav-btn"
                            style={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
                            onClick={() => swiperRef.current?.slidePrev()}
                            aria-label="Previous"
                        >
                            <i className="bi bi-chevron-left" />
                        </button>
                        <div style={{ paddingLeft: 40, paddingRight: 40 }}>
                            <Swiper
                                key={view}
                                modules={[Navigation]}
                                slidesPerView="auto"
                                spaceBetween={12}
                                onSwiper={(s) => { swiperRef.current = s; }}
                            >
                                {ads.map((ad, idx) => (
                                    <SwiperSlide key={ad.ad_id ?? idx} style={{ width: 280 }}>
                                        <div className="ads-slide">
                                            <AdCard ad={cardAd(ad)} contentLoaded={leaves !== undefined} mode="compact" menuActions={[]} hideMenu hideType />
                                            <div className="ads-slide__engagements-pill">
                                                <span className="ads-slide__engagements-value">
                                                    {(ad.engagements ?? 0).toLocaleString()}
                                                </span>
                                                <span className="ads-slide__engagements-label">engagements</span>
                                            </div>
                                            {ad.motivational_principles && (
                                                <div className="ads-slide__principles-pill" title={ad.motivational_principles}>
                                                    <span className="ads-slide__principles-label">Motivation Principles</span>
                                                    <span className="ads-slide__principles-value">
                                                        {ad.motivational_principles}
                                                    </span>
                                                </div>
                                            )}
                                            {ad.behavioral_strategies && (
                                                <div className="ads-slide__strategies-pill" title={ad.behavioral_strategies}>
                                                    <span className="ads-slide__strategies-label">Behavioral Strategies</span>
                                                    <span className="ads-slide__strategies-value">
                                                        {ad.behavioral_strategies}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        </div>
                        <button
                            type="button"
                            className="ads-nav-btn"
                            style={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
                            onClick={() => swiperRef.current?.slideNext()}
                            aria-label="Next"
                        >
                            <i className="bi bi-chevron-right" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
